import { activeResourceAtom } from "@app/resources/atom";
import { ActiveResource } from "@app/resources/components/ActiveResource";
import { ResourcesGridItem } from "@app/resources/components/ResourcesGridItem";
import { type Resource } from "@core/shared/types";
import { cn } from "@design-system/utils";
import { trans } from "@mongez/localization";
import { ChevronLeft, ChevronRight, FolderOpen } from "lucide-react";
import { useRef } from "react";

type ResourcesSectionProps = {
  groupedResources: Record<string, Resource[]>;
  className?: string;
};

export function ResourcesGrid({
  groupedResources,
  className,
}: ResourcesSectionProps) {
  const categories = Object.keys(groupedResources);
  const activeResource = activeResourceAtom.useValue();
  const carouselRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const scrollCarousel = (category: string, direction: -1 | 1) => {
    const carousel = carouselRefs.current[category];
    if (!carousel) return;

    const distance = Math.max(280, carousel.clientWidth * 0.75);
    carousel.scrollBy({
      behavior: "smooth",
      left: direction * distance,
    });
  };

  if (categories.length === 0) {
    return (
      <div className={cn("py-12 text-center", className)}>
        <div className="mx-auto max-w-md">
          <div className="mx-auto mb-6 w-fit rounded-full bg-sky-100 p-6 dark:bg-sky-950/40">
            <FolderOpen className="h-12 w-12 text-sky-500" />
          </div>
          <h3 className="mb-2 text-xl font-semibold text-slate-700 dark:text-slate-100">
            {trans("grades.noResourcesAvailable")}
          </h3>
          <p className="text-slate-500 dark:text-slate-400">
            {trans("grades.noResourcesAvailableDescription")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-8", className)}>
      {categories.map((category) => {
        const resources = groupedResources[category];
        const resourcesCount = resources.length;
        const hasActiveResource = resources.some(
          (resource) => resource.id === activeResource?.id,
        );

        return (
          <section
            key={category}
            className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5"
          >
            <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-600 dark:bg-sky-950 dark:text-sky-300">
                  <FolderOpen className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <h3 className="truncate text-lg font-black text-slate-950 dark:text-white">
                    {category}
                  </h3>
                  <p className="mt-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
                    {trans("itemsCount", { count: resourcesCount })}
                  </p>
                </div>
              </div>

              {resourcesCount > 1 && (
                <div className="flex items-center gap-2" dir="ltr">
                  <CarouselButton
                    label={trans("grades.previousResources")}
                    onClick={() => scrollCarousel(category, -1)}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </CarouselButton>
                  <CarouselButton
                    label={trans("grades.nextResources")}
                    onClick={() => scrollCarousel(category, 1)}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </CarouselButton>
                </div>
              )}
            </header>

            <div
              ref={(element) => {
                carouselRefs.current[category] = element;
              }}
              className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3 [scrollbar-color:rgb(14_165_233)_transparent] [scrollbar-width:thin]"
              dir="rtl"
            >
              {resources.map((resource, index) => (
                <div
                  className="w-[82%] shrink-0 snap-start sm:w-[46%] lg:w-[31%] xl:w-[23%]"
                  key={resource.id || index}
                >
                  <ResourcesGridItem resource={resource} />
                </div>
              ))}
            </div>

            {hasActiveResource && (
              <div className="mt-5">
                <ActiveResource />
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

type CarouselButtonProps = {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
};

function CarouselButton({ children, label, onClick }: CarouselButtonProps) {
  return (
    <button
      aria-label={label}
      className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:-translate-y-0.5 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-600 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-sky-700 dark:hover:bg-sky-950"
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}
